import React from 'react';

/** Segment-specific colors for avatar variants. */
export const SEGMENT_COLORS = ['#3eb298', '#5a69c1', '#8f6fc2', '#d4864e', '#c25a7a', '#5a9ec1'];

interface AvatarSvgProps {
  /** Selects one of 6 visual variants (0-5). Defaults to 5 (simple silhouette). */
  variant?: number;
  /** Override the fill color. Falls back to SEGMENT_COLORS[variant] or #D9D9D9. */
  color?: string;
  className?: string;
}

/**
 * Avatar SVG with 6 distinct variants used across report slides.
 */
const AvatarSvg: React.FC<AvatarSvgProps> = ({ variant = 5, color, className }) => {
  const safeVariant = Math.abs(variant) % 6;
  const fill = color ?? SEGMENT_COLORS[safeVariant] ?? '#D9D9D9';

  switch (safeVariant) {
    // variant 0: Business person (tie)
    case 0:
      return (
        <svg className={className} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="13" r="7" fill={fill} />
          <path d="M8 36c0-7 5-13 12-13s12 6 12 13" fill={fill} />
          <path d="M18 26l2 5 2-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="20" y1="31" x2="20" y2="35" stroke="#fff" strokeWidth="1" />
        </svg>
      );

    // variant 1: Long hair
    case 1:
      return (
        <svg className={className} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="13" r="7" fill={fill} />
          <path d="M13 12c0-5 3-9 7-9s7 4 7 9" fill={fill} />
          <path d="M13 12v6c0 0-1 3-1 5" stroke={fill} strokeWidth="2" strokeLinecap="round" />
          <path d="M27 12v6c0 0 1 3 1 5" stroke={fill} strokeWidth="2" strokeLinecap="round" />
          <path d="M8 36c0-7 5-13 12-13s12 6 12 13" fill={fill} />
        </svg>
      );

    // variant 2: Glasses
    case 2:
      return (
        <svg className={className} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="13" r="7" fill={fill} />
          <path d="M8 36c0-7 5-13 12-13s12 6 12 13" fill={fill} />
          <circle cx="16" cy="13" r="3" stroke="#fff" strokeWidth="1.2" fill="none" />
          <circle cx="24" cy="13" r="3" stroke="#fff" strokeWidth="1.2" fill="none" />
          <line x1="19" y1="13" x2="21" y2="13" stroke="#fff" strokeWidth="1.2" />
        </svg>
      );

    // variant 3: Headset
    case 3:
      return (
        <svg className={className} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="13" r="7" fill={fill} />
          <path d="M8 36c0-7 5-13 12-13s12 6 12 13" fill={fill} />
          <path d="M11 13a9 9 0 0118 0" stroke="#fff" strokeWidth="1.5" fill="none" />
          <rect x="10" y="11" width="3" height="5" rx="1.5" fill="#fff" />
          <rect x="27" y="11" width="3" height="5" rx="1.5" fill="#fff" />
          <path d="M10 16c-1 0-2 1-2 2v1" stroke="#fff" strokeWidth="1" strokeLinecap="round" />
        </svg>
      );

    // variant 4: Cap / hat
    case 4:
      return (
        <svg className={className} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="15" r="7" fill={fill} />
          <path d="M8 36c0-7 5-13 12-13s12 6 12 13" fill={fill} />
          <path d="M12 11h16c0-4-3-7-8-7s-8 3-8 7z" fill={fill} />
          <rect x="10" y="10" width="20" height="2.5" rx="1" fill="#fff" opacity="0.6" />
        </svg>
      );

    // variant 5 (default): Simple silhouette
    default:
      return (
        <svg className={className} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="14" r="7" fill={fill} />
          <path d="M8 36c0-7 5-13 12-13s12 6 12 13" fill={fill} />
        </svg>
      );
  }
};

export default AvatarSvg;
